import type { ResultOf } from "@opensea/graphql"
import { graphql, readFragment, useQuery } from "@opensea/graphql"
import {
  BottomSheetBody,
  BottomSheetHeader,
  BottomSheetHeaderTitle,
  Flex,
  Separator,
  useToasts,
} from "@opensea/ui-kit"
import { useCallback } from "react"
import { useTranslations } from "use-intl"
import { useSnapshot } from "valtio"
import { NETWORK_ONLY_OPERATION_CONTEXT } from "../../../constants"
import { useAddress } from "../../../polychain/hooks/useAddress"
import type { SchedulableResponse } from "../../../polychain/hooks/useScheduler"
import type { TransactionIdentifier } from "../../../stores/currentTransactionStore"
import { FlowActions, FlowState } from "../../../stores/flowStore"
import type { CollectionSweepFlowState } from "../../flow-state"
import { useAllActionsCompleted } from "../../hooks/useAllActionsCompleted"
import {
  useSetItemImageMap,
  useSetItemImageMapFragment,
} from "../../hooks/useSetItemImageMap"
import {
  useSetQuoteItems,
  useSetQuoteItemsFragment,
} from "../../hooks/useSetQuoteItems"
import {
  useSetTotalUsdPrice,
  useSetTotalUsdPriceFragment,
} from "../../hooks/useSetTotalUsdPrice"
import { CheckoutPaymentSection } from "../../PaymentSection/CheckoutPaymentSection"
import { CollectionSweepToast } from "../../ToastHandler/CollectionSweepToast/CollectionSweepToast"
import { CollectionSweepFlow_quoteFragment } from "./CollectionSweepFlow.fragment"
import { CollectionSweepFlowSkeleton } from "./CollectionSweepFlowSkeleton"
import { CollectionSweepReceipt } from "./components/CollectionSweepReceipt"
import { CollectionSweepFlowStatus } from "./components/CollectionSweepStatus"
import { CollectionSweepTimeline } from "./components/CollectionSweepTimeline/CollectionSweepTimeline"

type Props = Exclude<CollectionSweepFlowState, "mode">

const query = graphql(
  `
    query CollectionSweepQuoteFlowQuery(
      $collectionSlug: String!
      $maxAmountOfItems: Int!
      $maxPricePerItem: BigDecimal!
      $buyer: Address
    ) {
      sweepCollectionQuote(
        collectionSlug: $collectionSlug
        maxAmountOfItems: $maxAmountOfItems
        maxPricePerItem: $maxPricePerItem
        buyer: $buyer
      ) {
        ...CollectionSweepFlow_quote

        quote {
          ... on CollectionSweepQuoteSuccess {
            itemQuotes {
              ...useSetQuoteItems
              ...useSetItemImageMap
              ...useSetTotalUsdPrice
            }
          }
        }
      }
    }
  `,
  [
    CollectionSweepFlow_quoteFragment,
    useSetQuoteItemsFragment,
    useSetItemImageMapFragment,
    useSetTotalUsdPriceFragment,
  ],
)

type CollectionSweepQuoteSuccess = (ResultOf<
  typeof query
>["sweepCollectionQuote"]["quote"] & {
  __typename: "CollectionSweepQuoteSuccess"
})["itemQuotes"]

function getSuccessfulItemQuotes(
  data: ResultOf<typeof query> | null | undefined,
): CollectionSweepQuoteSuccess | undefined {
  if (
    !data?.sweepCollectionQuote.quote ||
    data.sweepCollectionQuote.quote.__typename !== "CollectionSweepQuoteSuccess"
  ) {
    return undefined
  }

  return data.sweepCollectionQuote.quote.itemQuotes
}

export function CollectionSweepFlow({
  collectionSlug,
  maxAmountOfItems,
  maxPricePerItem,
}: Props) {
  const t = useTranslations("wallet.CollectionSweepFlow")
  const address = useAddress()
  const { addCustomToast } = useToasts()

  const [{ data, error, fetching }] = useQuery({
    query,
    variables: {
      collectionSlug,
      maxAmountOfItems,
      maxPricePerItem: maxPricePerItem.toString(),
      buyer: address,
    },
    context: NETWORK_ONLY_OPERATION_CONTEXT,
  })

  const itemQuotes = getSuccessfulItemQuotes(data)
  const bestItemQuotes = itemQuotes?.slice(0, maxAmountOfItems)
  useSetQuoteItems(bestItemQuotes)
  useSetItemImageMap(itemQuotes) // use all returned quotes for images in case not all best items are bought
  useSetTotalUsdPrice(bestItemQuotes)

  const { flow } = useSnapshot(FlowState)
  const mode = (flow as CollectionSweepFlowState)?.mode

  const [allActionsCompleted] = useAllActionsCompleted()

  const handleAllActionsCompleted = useCallback(
    (
      _response: SchedulableResponse,
      receiptTransactions: TransactionIdentifier[],
    ) => {
      // Only show toast if mode is minimized
      if (mode === "minimized" && receiptTransactions.length > 0) {
        const { itemQuotes, itemImageMap, flow } = FlowState

        addCustomToast(
          () => (
            <CollectionSweepToast
              flow={flow}
              itemImageMap={itemImageMap}
              itemQuotes={itemQuotes}
              transactionIdentifiers={receiptTransactions}
            />
          ),
          {
            id: "collection-sweep-toast",
            duration: 15_000,
          },
        )

        // End the flow after toast is shown
        FlowActions.endFlow()
      }
    },
    [mode, addCustomToast],
  )

  if (fetching) {
    return <CollectionSweepFlowSkeleton count={1} />
  }

  if (error || !data?.sweepCollectionQuote) {
    throw new Error("There was a problem with collection sweep")
  }

  const quote = readFragment(
    CollectionSweepFlow_quoteFragment,
    data.sweepCollectionQuote,
  )

  return (
    <>
      {allActionsCompleted ? (
        <BottomSheetBody>
          <CollectionSweepFlowStatus
            collectionImageUrl={quote.collection.imageUrl}
          />
        </BottomSheetBody>
      ) : (
        <>
          <BottomSheetHeader>
            <BottomSheetHeaderTitle>{t("title")}</BottomSheetHeaderTitle>
          </BottomSheetHeader>

          <BottomSheetBody>
            <Flex className="size-full flex-col gap-6 md:flex-row md:gap-10">
              <CollectionSweepReceipt
                maxAmountOfItems={maxAmountOfItems}
                quote={quote}
              />

              <Separator
                className="hidden h-auto md:block"
                orientation="vertical"
              />

              <CheckoutPaymentSection
                cardSupported={false}
                currencySelector={null}
                timeline={
                  <CollectionSweepTimeline
                    collectionSlug={collectionSlug}
                    maxAmountOfItems={maxAmountOfItems}
                    maxPricePerItem={maxPricePerItem}
                    onAllActionsCompleted={handleAllActionsCompleted}
                    quote={quote}
                  />
                }
              />
            </Flex>
          </BottomSheetBody>
        </>
      )}
    </>
  )
}
